import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { updateProfile } from 'firebase/auth';
import { firestore, storage, auth } from './firebase/config';
import { useAuth } from './contexts/AuthContext';
import { createStoragePath } from './firebase/adapters';

const UserProfileEdit = () => {
  const { currentUser, isAuthenticated, isRegularUser } = useAuth();
  const navigate = useNavigate();
  
  // Profile data states
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [aboutMe, setAboutMe] = useState('');
  const [education, setEducation] = useState('');
  const [educationDegree, setEducationDegree] = useState('');
  const [languages, setLanguages] = useState([]);
  const [customLanguage, setCustomLanguage] = useState('');
  const [location, setLocation] = useState('');
  const [skills, setSkills] = useState([]);
  const [customSkill, setCustomSkill] = useState('');
  
  // Additional profile fields to match database
  const [phoneNumber, setPhoneNumber] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [expectedRate, setExpectedRate] = useState('');
  const [workAvailability, setWorkAvailability] = useState('');
  const [workPeriod, setWorkPeriod] = useState('');
  const [desiredJobs, setDesiredJobs] = useState([]);
  const [customDesiredJob, setCustomDesiredJob] = useState('');
  
  // Profile image states
  const [profileImage, setProfileImage] = useState(null);
  const [profileImageUrl, setProfileImageUrl] = useState('');
  const [originalImageUrl, setOriginalImageUrl] = useState('');
  
  // UI states
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  // Lists for suggestions
  const popularSchools = [
    'Harvard University',
    'Stanford University',
    'MIT',
    'Yale University',
    'Princeton University',
    'Columbia University',
    'University of California, Berkeley',
    'University of Chicago',
    'New York University',
    'Cornell University',
    'UCLA',
    'University of Michigan',
    'Duke University',
    'University of Pennsylvania'
  ];
  
  const commonLanguages = [
    'English',
    'Spanish',
    'Mandarin',
    'Cantonese',
    'French',
    'German',
    'Japanese',
    'Korean',
    'Italian',
    'Russian',
    'Portuguese',
    'Arabic'
  ];
  
  const educationDegrees = [
    'High School Diploma',
    'Associate Degree',
    'Bachelor\'s Degree',
    'Master\'s Degree',
    'PhD',
    'Trade School Certificate',
    'Other'
  ];
  
  const commonSkills = [
    'Cooking', 'Food Preparation', 'Bartending', 'Serving', 'Hosting',
    'Customer Service', 'Cash Handling', 'Food Safety', 'Cleaning',
    'Inventory Management', 'Team Leadership', 'POS Systems',
    'Menu Knowledge', 'Wine Knowledge', 'Barista Skills'
  ];
  
  // Popular cities for location suggestions
  const popularCities = [
    'New York, NY, USA',
    'Los Angeles, CA, USA',
    'Chicago, IL, USA',
    'Houston, TX, USA',
    'Phoenix, AZ, USA',
    'Philadelphia, PA, USA',
    'San Antonio, TX, USA',
    'San Diego, CA, USA',
    'Dallas, TX, USA',
    'San Jose, CA, USA',
    'Austin, TX, USA',
    'Jacksonville, FL, USA',
    'San Francisco, CA, USA',
    'Seattle, WA, USA',
    'Denver, CO, USA',
    'Boston, MA, USA',
    'Washington, DC, USA',
    'Nashville, TN, USA',
    'Las Vegas, NV, USA'
  ];
  
  // Load user profile on component mount
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        if (!isAuthenticated || !isRegularUser || !currentUser) {
          navigate('/login');
          return;
        }
        
        const userProfileDoc = await getDoc(doc(firestore, 'userProfiles', currentUser.uid));
        if (userProfileDoc.exists()) {
          const profileData = userProfileDoc.data();
          
          // Set basic profile data
          setFirstName(profileData.firstName || '');
          setLastName(profileData.lastName || '');
          setAboutMe(profileData.aboutMe || '');
          setEducation(profileData.education || '');
          setEducationDegree(profileData.educationDegree || '');
          setLanguages(profileData.languages || []);
          setLocation(profileData.location || '');
          setSkills(profileData.skills || []);
          
          // Additional fields
          setPhoneNumber(profileData.phoneNumber || '');
          setJobTitle(profileData.jobTitle || '');
          setExpectedRate(profileData.expectedRate || '');
          setWorkAvailability(profileData.workAvailability || '');
          setWorkPeriod(profileData.workPeriod || '');
          setDesiredJobs(profileData.desiredJobs || []);
          
          // Handle profile image
          if (profileData.photoURL) {
            setProfileImageUrl(profileData.photoURL);
            setOriginalImageUrl(profileData.photoURL);
          } else if (currentUser.photoURL) {
            setProfileImageUrl(currentUser.photoURL);
            setOriginalImageUrl(currentUser.photoURL);
          }
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
        setError('Failed to load profile data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchUserProfile();
  }, [currentUser, isAuthenticated, isRegularUser, navigate]);
  
  // Handle file selection for profile image
  const handleImageChange = (e) => {
    if (e.target.files[0]) {
      setProfileImage(e.target.files[0]);
      setProfileImageUrl(URL.createObjectURL(e.target.files[0]));
    }
  };
  
  // Add or remove a language
  const handleLanguageToggle = (language) => {
    if (languages.includes(language)) {
      setLanguages(languages.filter(item => item !== language));
    } else {
      setLanguages([...languages, language]);
    }
  };
  
  // Add custom language
  const addCustomLanguage = () => {
    if (customLanguage.trim() && !languages.includes(customLanguage.trim())) {
      setLanguages([...languages, customLanguage.trim()]);
      setCustomLanguage('');
    }
  };
  
  // Add or remove a skill
  const handleSkillToggle = (skill) => {
    if (skills.includes(skill)) {
      setSkills(skills.filter(item => item !== skill));
    } else {
      setSkills([...skills, skill]);
    }
  };
  
  // Add custom skill
  const addCustomSkill = () => {
    if (customSkill.trim() && !skills.includes(customSkill.trim())) {
      setSkills([...skills, customSkill.trim()]);
      setCustomSkill('');
    }
  };
  
  // Add or remove a desired job
  const handleDesiredJobToggle = (job) => {
    if (desiredJobs.includes(job)) {
      setDesiredJobs(desiredJobs.filter(item => item !== job));
    } else {
      setDesiredJobs([...desiredJobs, job]);
    }
  };
  
  // Add custom desired job
  const addCustomDesiredJob = () => {
    if (customDesiredJob.trim() && !desiredJobs.includes(customDesiredJob.trim())) {
      setDesiredJobs([...desiredJobs, customDesiredJob.trim()]);
      setCustomDesiredJob('');
    }
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isAuthenticated || !currentUser) {
      setError('You must be logged in to update your profile');
      return;
    }
    
    setError('');
    setSuccessMessage('');
    setSaving(true);
    
    try {
      // Create the profile data object
      const profileData = {
        firstName,
        lastName,
        aboutMe,
        education,
        educationDegree,
        languages,
        location,
        skills,
        phoneNumber,
        jobTitle,
        expectedRate,
        workAvailability,
        workPeriod,
        desiredJobs,
        email: currentUser.email,
        updatedAt: new Date()
      };
      
      // Handle profile image upload if a new one was selected
      if (profileImage) {
        try {
          // Use the adapter function to create proper storage path format
          const storagePath = createStoragePath('userProfilePhoto', currentUser.uid, 'profile');
          console.log(`Uploading profile image to: ${storagePath}`);
          
          // Upload to Firebase Storage
          const storageRef = ref(storage, storagePath);
          await uploadBytes(storageRef, profileImage);
          const downloadURL = await getDownloadURL(storageRef);
          
          // Add URL to profile data
          profileData.photoURL = downloadURL;
          
          // Update Auth profile with new photo URL
          await updateProfile(auth.currentUser, {
            photoURL: downloadURL
          });
          
          console.log('Profile image uploaded successfully');
        } catch (imageError) {
          console.error('Error uploading profile image:', imageError);
          setError('Failed to upload profile image. Your other profile information will still be saved.');
        }
      } else if (originalImageUrl) {
        // Keep the original image URL if no new image was selected
        profileData.photoURL = originalImageUrl;
      }
      
      // Save profile data to Firestore
      await setDoc(doc(firestore, 'userProfiles', currentUser.uid), profileData, { merge: true });
      
      setSuccessMessage('Profile updated successfully!');
      setTimeout(() => {
        navigate('/dashboard/home');
      }, 1500);
    } catch (error) {
      console.error('Error updating profile:', error);
      setError('Failed to update profile. Please try again later.');
    } finally {
      setSaving(false);
    }
  };
  
  // Cancel and go back to dashboard
  const handleCancel = () => {
    navigate('/dashboard/home');
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#FF8C00]"></div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4 max-w-3xl">
        <div className="bg-white rounded-lg shadow-lg p-6 md:p-8">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-gray-800">Edit Your Profile</h1>
            <button
              onClick={() => navigate('/dashboard/home')}
              className="text-[#FF8C00] hover:underline flex items-center"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
              </svg>
              Back to Dashboard
            </button>
          </div>
          
          {error && (
            <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-md">
              {error}
            </div>
          )}
          
          {successMessage && (
            <div className="mb-6 p-4 bg-green-50 text-green-700 rounded-md">
              {successMessage}
            </div>
          )}
          
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              {/* Profile Image */}
              <div className="md:col-span-2 flex flex-col items-center">
                <div className="w-32 h-32 rounded-full bg-gray-200 flex items-center justify-center mb-4 overflow-hidden">
                  {profileImageUrl ? (
                    <img src={profileImageUrl} alt="Profile" className="w-full h-full object-cover" />
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  )}
                </div>
                <div className="flex items-center">
                  <input
                    id="profileImage"
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="hidden"
                  />
                  <label htmlFor="profileImage" className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md cursor-pointer hover:bg-gray-200 transition-colors">
                    {profileImageUrl ? 'Change Photo' : 'Upload Photo'}
                  </label>
                  {profileImageUrl && (
                    <button
                      type="button"
                      onClick={() => {
                        setProfileImage(null);
                        setProfileImageUrl('');
                      }}
                      className="ml-2 px-4 py-2 text-red-500 hover:text-red-700 transition-colors"
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
              
              {/* Basic Information */}
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                  First Name
                </label>
                <input
                  id="firstName"
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF8C00]"
                />
              </div>
              
              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                  Last Name
                </label>
                <input
                  id="lastName"
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF8C00]"
                />
              </div>
              
              <div className="md:col-span-2">
                <label htmlFor="aboutMe" className="block text-sm font-medium text-gray-700 mb-1">
                  About Me
                </label>
                <textarea
                  id="aboutMe"
                  value={aboutMe}
                  onChange={(e) => setAboutMe(e.target.value)}
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF8C00]"
                  placeholder="Tell us a bit about yourself, your interests, and what you're looking for"
                ></textarea>
              </div>
              
              {/* Education */}
              <div>
                <label htmlFor="education" className="block text-sm font-medium text-gray-700 mb-1">
                  Education
                </label>
                <input
                  id="education"
                  type="text"
                  value={education}
                  onChange={(e) => setEducation(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF8C00]"
                  placeholder="School or university name"
                  list="schools-list"
                />
                <datalist id="schools-list">
                  {popularSchools.map((school, index) => (
                    <option key={index} value={school} />
                  ))}
                </datalist>
              </div>
              
              <div>
                <label htmlFor="educationDegree" className="block text-sm font-medium text-gray-700 mb-1">
                  Degree
                </label>
                <select 
                  id="educationDegree" 
                  value={educationDegree}
                  onChange={(e) => setEducationDegree(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF8C00]"
                >
                  <option value="">Select a degree type</option>
                  {educationDegrees.map((degree, index) => (
                    <option key={index} value={degree}>{degree}</option>
                  ))}
                </select>
              </div>
              
              {/* Work Location */}
              <div className="md:col-span-2">
                <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-1">
                  Preferred Work Location
                </label>
                <input
                  id="location"
                  type="text"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF8C00]"
                  placeholder="City, State"
                  list="cities-list"
                />
                <datalist id="cities-list">
                  {popularCities.map((city, index) => (
                    <option key={index} value={city} />
                  ))}
                </datalist>
              </div>
              
              {/* Desired Jobs */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Desired Job Types
                </label>
                <div className="flex flex-wrap gap-2 mb-3">
                  {['Server', 'Bartender', 'Chef', 'Cook', 'Host/Hostess', 'Dishwasher', 'Manager', 'Cashier', 'Barista', 'Kitchen Staff'].map((job) => (
                    <button
                      key={job}
                      type="button"
                      onClick={() => handleDesiredJobToggle(job)}
                      className={`px-3 py-1 text-sm rounded-md ${
                        desiredJobs.includes(job)
                          ? 'bg-blue-500 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      } transition-colors`}
                    >
                      {job}
                    </button>
                  ))}
                </div>
                
                <div className="flex">
                  <input
                    type="text"
                    value={customDesiredJob}
                    onChange={(e) => setCustomDesiredJob(e.target.value)}
                    placeholder="Add another job type"
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-l-md focus:outline-none focus:ring-2 focus:ring-[#FF8C00]"
                  />
                  <button
                    type="button"
                    onClick={addCustomDesiredJob}
                    className="px-4 py-2 bg-blue-500 text-white rounded-r-md hover:bg-blue-600 transition-colors"
                  >
                    Add
                  </button>
                </div>
                
                {desiredJobs.length > 0 && (
                  <div className="mt-3">
                    <p className="text-sm text-gray-600 mb-2">Selected Job Types:</p>
                    <div className="flex flex-wrap gap-2">
                      {desiredJobs.map((job, index) => (
                        <span key={index} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {job}
                          <button
                            type="button"
                            onClick={() => handleDesiredJobToggle(job)}
                            className="ml-1.5 text-blue-600 hover:text-blue-800"
                          >
                            &times;
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Languages */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Languages
                </label>
                <div className="flex flex-wrap gap-2 mb-3">
                  {commonLanguages.map((language) => (
                    <button
                      key={language}
                      type="button"
                      onClick={() => handleLanguageToggle(language)}
                      className={`px-3 py-1 text-sm rounded-md ${
                        languages.includes(language)
                          ? 'bg-[#FF8C00] text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      } transition-colors`}
                    >
                      {language}
                    </button>
                  ))}
                </div>
                
                <div className="flex">
                  <input
                    type="text"
                    value={customLanguage}
                    onChange={(e) => setCustomLanguage(e.target.value)}
                    placeholder="Add another language"
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-l-md focus:outline-none focus:ring-2 focus:ring-[#FF8C00]"
                  />
                  <button
                    type="button"
                    onClick={addCustomLanguage}
                    className="px-4 py-2 bg-[#FF8C00] text-white rounded-r-md hover:bg-[#FF8C00]/90 transition-colors"
                  >
                    Add
                  </button>
                </div>
                
                {languages.length > 0 && (
                  <div className="mt-3">
                    <p className="text-sm text-gray-600 mb-2">Selected Languages:</p>
                    <div className="flex flex-wrap gap-2">
                      {languages.map((language, index) => (
                        <span key={index} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#FF8C00]/10 text-[#FF8C00]">
                          {language}
                          <button
                            type="button"
                            onClick={() => handleLanguageToggle(language)}
                            className="ml-1.5 text-[#FF8C00] hover:text-[#FF8C00]/70"
                          >
                            &times;
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Skills */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Skills
                </label>
                <div className="flex flex-wrap gap-2 mb-3">
                  {commonSkills.map((skill) => (
                    <button
                      key={skill}
                      type="button"
                      onClick={() => handleSkillToggle(skill)}
                      className={`px-3 py-1 text-sm rounded-md ${
                        skills.includes(skill)
                          ? 'bg-[#FF8C00] text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      } transition-colors`}
                    >
                      {skill}
                    </button>
                  ))}
                </div>
                
                <div className="flex">
                  <input
                    type="text"
                    value={customSkill}
                    onChange={(e) => setCustomSkill(e.target.value)}
                    placeholder="Add another skill"
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-l-md focus:outline-none focus:ring-2 focus:ring-[#FF8C00]"
                  />
                  <button
                    type="button"
                    onClick={addCustomSkill}
                    className="px-4 py-2 bg-[#FF8C00] text-white rounded-r-md hover:bg-[#FF8C00]/90 transition-colors"
                  >
                    Add
                  </button>
                </div>
                
                {skills.length > 0 && (
                  <div className="mt-3">
                    <p className="text-sm text-gray-600 mb-2">Selected Skills:</p>
                    <div className="flex flex-wrap gap-2">
                      {skills.map((skill, index) => (
                        <span key={index} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#FF8C00]/10 text-[#FF8C00]">
                          {skill}
                          <button
                            type="button"
                            onClick={() => handleSkillToggle(skill)}
                            className="ml-1.5 text-[#FF8C00] hover:text-[#FF8C00]/70"
                          >
                            &times;
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Contact Information */}
              <div>
                <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number
                </label>
                <input
                  id="phoneNumber"
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF8C00]"
                  placeholder="(555) 123-4567"
                />
              </div>
              
              <div>
                <label htmlFor="jobTitle" className="block text-sm font-medium text-gray-700 mb-1">
                  Current/Desired Job Title
                </label>
                <input
                  id="jobTitle"
                  type="text"
                  value={jobTitle}
                  onChange={(e) => setJobTitle(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF8C00]"
                  placeholder="e.g., Server, Chef, Manager"
                />
              </div>
              
              {/* Work Preferences */}
              <div>
                <label htmlFor="expectedRate" className="block text-sm font-medium text-gray-700 mb-1">
                  Expected Hourly Rate ($)
                </label>
                <input
                  id="expectedRate"
                  type="number"
                  value={expectedRate}
                  onChange={(e) => setExpectedRate(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF8C00]"
                  placeholder="15"
                  min="0"
                  step="0.50"
                />
              </div>
              
              <div>
                <label htmlFor="workAvailability" className="block text-sm font-medium text-gray-700 mb-1">
                  Work Availability
                </label>
                <select
                  id="workAvailability"
                  value={workAvailability}
                  onChange={(e) => setWorkAvailability(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF8C00]"
                >
                  <option value="">Select availability</option>
                  <option value="Full-time">Full-time</option>
                  <option value="Part-time">Part-time</option>
                  <option value="Weekends only">Weekends only</option>
                  <option value="Evenings only">Evenings only</option>
                  <option value="Flexible">Flexible</option>
                </select>
              </div>
              
              <div className="md:col-span-2">
                <label htmlFor="workPeriod" className="block text-sm font-medium text-gray-700 mb-1">
                  Preferred Work Period
                </label>
                <input
                  id="workPeriod"
                  type="text"
                  value={workPeriod}
                  onChange={(e) => setWorkPeriod(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF8C00]"
                  placeholder="e.g., Monday-Friday 9AM-5PM, Weekends, Evenings"
                />
              </div>
            </div>
            
            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={handleCancel}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-6 py-2 bg-[#FF8C00] text-white rounded-md hover:bg-[#FF8C00]/90 transition-colors disabled:opacity-70 flex items-center"
              >
                {saving ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Saving...
                  </>
                ) : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UserProfileEdit; 